import { RefObject, useEffect, useMemo, useRef, useState } from "react";
import Map, { MapRef, Marker } from "react-map-gl";
import BottomNavigation from "@/components/bottom-navigation";
import Vehicle from "@/assets/icons/vehicle-filled.svg?react";
import Icon from "@/assets/icons/icon";
import { useGetVehicleLocationQuery } from "@/generated/graphql";

const MAPBOX_TOKEN = import.meta.env.VITE_PUBLIC_MAPBOX_TOKEN!;

const MapPage = () => {
  const mapRef = useRef<MapRef>();

  const [userLocation, setUserLocation] = useState<{
    latitude: number;
    longitude: number;
  } | null>(null);

  const [{ data: vehicleLocation, fetching: _locationFetching }] =
    useGetVehicleLocationQuery();
  // const vehicleLocation = { latitude: 58.071, longitude: 11.823 };

  const vehicleLocationData = useMemo(() => {
    if (!vehicleLocation) return undefined;

    return vehicleLocation.currentEndUser.characteristics.deviceCharacteristics
      .geolocation.current;
  }, [vehicleLocation]);

  mapRef.current?.on("load", () => {
    if (!vehicleLocationData) return;
    mapRef.current?.jumpTo({
      center: [vehicleLocationData.longitude, vehicleLocationData.latitude],
      zoom: 13,
    });
  });

  const chargingStations: Array<{ latitude: number; longitude: number }> = [
    // As of now, there is no charging stations. But they can be added here when we do
  ];

  useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setUserLocation({
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
          });
        },
        (error) => {
          console.error("Error obtaining location:", error);
          // Handle location error (e.g., user denied the request)
        },
      );
    } else {
      console.error("Geolocation is not supported by your browser");
    }
  }, []);

  return (
    <div className="h-full flex flex-col fixed">
      <div className="relative h-full w-full flex overflow-auto">
        <Map
          ref={mapRef as RefObject<MapRef>}
          style={{ width: "100%", height: "100%" }}
          mapStyle="mapbox://styles/mapbox/light-v11"
          mapboxAccessToken={MAPBOX_TOKEN}
        >
          {userLocation && (
            <UserMarker
              longitude={userLocation.longitude}
              latitude={userLocation.latitude}
            />
          )}
          {vehicleLocationData && (
            <VehicleMarker
              longitude={vehicleLocationData.longitude}
              latitude={vehicleLocationData.latitude}
            />
          )}
          {chargingStations.map((station, index) => (
            <ChargingStationMarker
              key={index}
              longitude={station.longitude}
              latitude={station.latitude}
            />
          ))}
        </Map>
      </div>
      <div>
        <BottomNavigation />
      </div>
    </div>
  );
};

const UserMarker = ({
  longitude,
  latitude,
}: {
  longitude: number;
  latitude: number;
}) => (
  <Marker longitude={longitude} latitude={latitude}>
    <div className="relative">
      <div
        className={
          "flex justify-center items-center bg-transparent w-16 h-16 rounded-full border-4 border-white animate-location"
        }
      ></div>
      <div
        className="absolute inset-0 m-auto bg-brand w-3 h-3 rounded-full"
        style={{
          boxShadow: "0 0 10px #029B00",
        }}
      />
    </div>
  </Marker>
);

const VehicleMarker = ({
  longitude,
  latitude,
}: {
  longitude: number;
  latitude: number;
}) => (
  <Marker longitude={longitude} latitude={latitude}>
    <div className="relative">
      <div
        className={
          "flex justify-center items-center bg-transparent rounded-full border-4 border-white w-16 h-16 animate-location"
        }
      ></div>

      <div
        className="absolute inset-0 m-auto flex justify-center items-center bg-brand w-6 h-6 rounded-full"
        style={{
          boxShadow: "0 0 10px #029B00",
        }}
      >
        <Vehicle color="white" height={16} width={16} />
        {/* <img className="fill-white" src="/vehicle_def.svg" alt="Profile" height={12} width={12} /> */}
      </div>
    </div>
  </Marker>
);

const ChargingStationMarker = ({
  longitude,
  latitude,
}: {
  longitude: number;
  latitude: number;
}) => (
  <Marker longitude={longitude} latitude={latitude}>
    <div className="relative h-9 w-7">
      <div className="absolute -translate-y-1/2 top-0 left-0 w-full h-full z-10">
        <Icon icon="ChargingStation" size={"28px"} />
      </div>
    </div>
    <div
      className="absolute inset-0 m-auto flex justify-center items-center bg-[#3da33c] w-3 h-3 rounded-full border-2 border-[#38CD37]"
      style={{
        boxShadow: "0 0 10px #029B00",
      }}
    />
  </Marker>
);

export default MapPage;
